import { useState } from "react";
import { Link2, Loader2, CheckCircle2, AlertCircle, ExternalLink } from "lucide-react";
import Lang from "../../components/Lang";
import useCTX from "../../hooks/useCTX";
import mut from "../../hooks/mut";
import { usePrimaryColor } from "../../components/PrimaryColorContext";


/**
 * LessonPlanLinks - Displays link submission boxes for teacher lesson plan uploads
 * @param {object} props
 * @param {object} props.lesson - Lesson record from DB
 * @param {object} props.cLesson - Curriculum lesson data
 * @returns {JSX.Element}
 * @author opencode (glm-5.1) + Forge
 * @version 2.0.0
 * @since 2026-04-13
 */
export default function LessonPlanLinks({ lesson, cLesson }) {
  const { primaryColor } = usePrimaryColor();
  return (
    <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
      <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
        <Link2 size={20} className={`text-${primaryColor}-800`} />
        <Lang w="linksToLessonPlans" />
      </h3>
      <div className="flex items-center gap-4 flex-wrap">
        {boxes.map((b) => (
          <LinkBox
            key={b.field}
            label={b.label}
            field={b.field}
            lesson={lesson}
            cLesson={cLesson}
          />
        ))}
      </div>
    </div>
  );
}

const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

/**
 * LinkBox - Individual link submission card with clipboard paste and status
 * @param {object} props
 * @param {string} props.label - Lang key for the link type label
 * @param {string} props.field - Field name on lesson object
 * @param {object} props.lesson - Lesson record
 * @param {object} props.cLesson - Curriculum lesson data
 * @returns {JSX.Element}
 * @author opencode (glm-5.1) + Forge
 * @version 2.0.0
 * @since 2026-04-13
 */
function LinkBox({ label, field, lesson, cLesson }) {
  const [error, setError] = useState("");
  const { primaryColor } = usePrimaryColor();
  const [status, setStatus] = useState(lesson[field] ? "success" : "idle");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(undefined);
  const { user } = useCTX();

  const update = async () => {
    let value;
    try {
      value = await navigator.clipboard.readText();
    } catch {
      setError("Could not read clipboard. Please copy the link and try again.");
      return;
    }

    if (!value) {
      setError("No link is pasted, please copy again");
      return;
    }
    if (!isValidUrl(value)) {
      setError("Must be a valid URL, please try copying it again");
      setStatus("error");
      return;
    } else {
      setError("");
    }

    const match = value.match(/\/document\/d\/([a-zA-Z0-9-_]+)/);
    if (!match) {
      setError("Must be a google document, please try copying it again");
      return;
    }

    try {
      setLoading(true);
      const res = await mut("post", "/copy-google-doc", {
        fileId: match[1],
        syllabus: cLesson.syllabus._id,
        syllabusName: cLesson.syllabus.name,
        unit: cLesson.unit._id,
        unitNumber: cLesson.unit.unitNumber,
        lesson: cLesson._id,
        lessonNumber: cLesson.lessonNumber,
        tDate: new Date(lesson.tDate).fDate(),
        name: user.name,
        field,
        lessonId: lesson._id,
      });
      if (res.status === 404) {
        setError(
          res.response.data.error.message.substring(0, 14) +
            ", check file is shared with everyone or htexplore123@gmail.com"
        );
        setLoading(false);
      } else {
        setStatus("success");
        setLoading(false);
        setSuccess(res);
      }
    } catch (err) {
      setStatus("error");
      setError("Couldn't contact server");
      setLoading(false);
    }
  };

  let link = `https://docs.google.com/document/d/`;
  if (lesson && lesson[field]) link += lesson[field];
  if (success) link += success.id;

  const statusConfig = {
    idle: {
      icon: <Link2 size={14} />,
      label: "Paste from clipboard",
      classes:
        `bg-${primaryColor}-800 text-white hover:bg-${primaryColor}-900`,
    },
    success: {
      icon: <CheckCircle2 size={14} />,
      label: "Submitted",
      classes: "bg-emerald-500 text-white hover:bg-emerald-600",
    },
    error: {
      icon: <AlertCircle size={14} />,
      label: "Error",
      classes: "bg-red-500 text-white hover:bg-red-600",
    },
  };

  const cfg = statusConfig[status] || statusConfig.idle;

  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="text-xs text-slate-500 font-medium whitespace-nowrap">
        <Lang w={label} />
      </span>
      <button
        onClick={update}
        disabled={loading}
        className={`inline-flex items-center gap-1.5 px-3 h-8 text-xs font-medium rounded-[10px] transition-all duration-150 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed ${cfg.classes}`}
      >
        {loading ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          cfg.icon
        )}
        {loading ? "Processing..." : cfg.label}
      </button>
      {(lesson[field] || success) ? (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-0.5 text-xs text-${primaryColor}-800 hover:underline`}
        >
          <ExternalLink size={11} />
          Open
        </a>
      ) : error ? (
        <span className="text-xs text-red-500">{error}</span>
      ) : (
        <span className="text-xs text-transparent">-</span>
      )}
    </div>
  );
}

const boxes = [
  {
    label: "vietnameseTeacher",
    field: "GVLessonPlan",
  },
  {
    label: "foreignTeacher",
    field: "GVNNLessonPlan",
  },
  {
    label: "teachingAssistant",
    field: "TALessonPlan",
  },
];
